import type { Ref } from "react"

import type { Asset as AssetType } from "~/types/asset"

import { Embed, type EmbedProps } from "./Embed"
import { Image, type ImageProps } from "./Image"
import { Video, type VideoProps } from "./Video"

type ImagePropsWithoutSource = Omit<ImageProps, "image" | "onLoad">

type VideoPropsWithoutSource = Omit<VideoProps, "video" | "onLoad">

type EmbedPropsWithoutSource = Omit<EmbedProps, "embed" | "onLoad">

export type SharedAssetProps = {
  /** @default "false" */
  hasPriority?: boolean

  /** @default "lazy" */
  loading?: "eager" | "lazy"

  /** @default "cover" */
  objectFit?: "cover" | "contain"

  /**
   * **Note**: This will only work if `hasPriority` is set to `false`.
   *
   * @default true
   */
  animateOnLoad?: boolean

  /**
   * This supports either a string color or `false` to disable it. Defaults
   * to `dark-gray`.
   */
  lazyLoadBackgroundColor?: false | string
}

export type AssetProps = {
  ref?: Ref<HTMLSpanElement>
  asset: AssetType | null
  onLoad?: (
    asset: HTMLImageElement | HTMLVideoElement | HTMLIFrameElement,
  ) => void
} & (
  | ImagePropsWithoutSource
  | VideoPropsWithoutSource
  | EmbedPropsWithoutSource
) &
  // Here we extract the props that only apply to each specific asset type
  Pick<ImageProps, "imageSizes" | "imageProps"> &
  Pick<VideoProps, "videoProps" | "isVideoPlaying"> &
  Pick<EmbedProps, "embedProps" | "useIframeAspectRatio">

export const Asset = ({ ref, asset, ...rest }: AssetProps) => {
  if (!asset) return null

  // Extract the props to ensure they don't get passed to the wrong component
  const {
    imageSizes,
    imageProps,
    isVideoPlaying,
    videoProps,
    embedProps,
    useIframeAspectRatio,
    ...restFiltered
  } = rest

  if (asset.kind === "image") {
    return (
      <Image
        ref={ref}
        image={asset}
        imageSizes={imageSizes}
        imageProps={imageProps}
        {...restFiltered}
      />
    )
  }

  if (asset.kind === "video") {
    return (
      <Video
        ref={ref}
        video={asset}
        videoProps={videoProps}
        isVideoPlaying={isVideoPlaying}
        {...restFiltered}
      />
    )
  }

  if (asset.kind === "json") {
    return (
      <Embed
        ref={ref}
        embed={asset}
        embedProps={embedProps}
        useIframeAspectRatio={useIframeAspectRatio}
        {...restFiltered}
      />
    )
  }

  console.warn(
    `[Asset] Unsupported type given: ${JSON.stringify(asset, null, 2)}`,
  )
  return null
}
